import { useState } from 'react';
import { failureOf } from '@/api/errors';
import { useTwinTrail, type TrailContext } from '@/api/hooks';
import type { WireAuditRow } from '@/api/wire';
import { startedBy } from './started';
import { actionWords, outcomeWords, targetWords } from '@/oversight/auditWords';
import { Dialog } from './Dialog';
import { Button, Skeleton } from './primitives';
import { Timeline, when, type TimelineEntry } from './records';
import { Refusal } from './Refusal';

/**
 * The viewer's own twin's trail, where they are looking (0122): what it
 * did in this department, project or task, read from the audit chain.
 * Everyone's, as the owner decided (ADR-0151): each person sees their own
 * twin and nobody else's, because the route finds the twin from the
 * person and the page never names one.
 */
export function TwinTrailButton({ context, where }: { context: TrailContext; where: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)} data-testid="twin-trail-open">
        Audit
      </Button>
      {open && <TrailDialog context={context} where={where} onClose={() => setOpen(false)} />}
    </>
  );
}

function TrailDialog({ context, where, onClose }: { context: TrailContext; where: string; onClose: () => void }) {
  const q = useTwinTrail(context, true);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Audit · your twin"
      title={`What your twin did in ${where}`}
      secondary={{ label: 'Close', onClick: onClose }}
      note="From the audit chain, the last ninety days"
    >
      {q.isPending && <Skeleton rows={5} />}
      {q.isError && <Refusal failure={failureOf(q.error)} />}
      {q.data && (
        <div data-testid="twin-trail">
          <Timeline
            title={`${q.data.rows.length} ${q.data.rows.length === 1 ? 'act' : 'acts'}`}
            entries={q.data.rows.map(entryOf)}
            note="Only your own twin, and on a task of theirs what others did to it. The whole log is on Audit in the Control room."
          />
          {(q.data.readsOfMe?.length ?? 0) > 0 && (
            <div data-testid="reads-of-me">
              <Timeline
                title={`Read by a superadmin’s twin: ${q.data.readsOfMe!.length}`}
                entries={q.data.readsOfMe!.map(readEntryOf)}
                note="A superadmin’s twin may read any person’s own data the platform holds (Amendment 0013). Every read is receipted to the person whose it is, and listed here."
              />
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** A read of this person's own data under the reach: who, for whom, what kind of record, and when. */
function readEntryOf(r: WireAuditRow): TimelineEntry {
  const kind = r.target.split(':')[2] ?? 'record';
  const what = kind === 'thread' ? 'one of your conversations' : kind === 'task' ? 'one of your tasks' : kind === 'project' ? 'a project of yours' : `a ${kind} of yours`;
  return {
    id: String(r.id),
    title: `Read ${what}`,
    body: `${r.actorLabel}${r.onBehalfOfLabel ? `, for ${r.onBehalfOfLabel}` : ''}`,
    when: when(r.at),
  };
}

function entryOf(r: WireAuditRow): TimelineEntry {
  const refused = r.outcome !== 'ok';
  return {
    id: String(r.id),
    title: actionWords(r.action),
    body: `${r.actorLabel} · ${targetWords(r)} · ${startedBy(r)}`,
    when: when(r.at),
    ...(refused ? { chip: outcomeWords(r) } : {}),
  };
}
